import { Box, ListItem, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { HistoryEventType } from "models/HistoryEventType";
import React from "react";

interface Props {
  event: HistoryEventType;
}

const CATEGORY_COLORS: Record<string, string> = {
  CardPlayed: "#4C79C5",
  CardTapped: "#C59B3B",
  PhaseUpdate: "#7E57C2",
  StandardAction: "#999CA3",
};

const HistoryEventRoot = styled(ListItem, {
  name: "HistoryEvent",
  slot: "Root",
})(({ theme }) => ({
  padding: theme.spacing(0.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderBottom: "none",
  },
  fontSize: "0.85em",
}));

const HistoryEventTimestamp = styled(Typography, {
  name: "HistoryEvent",
  slot: "Timestamp",
})(({ theme }) => ({
  color: theme.palette.text.secondary,
  paddingRight: theme.spacing(0.5),
  fontFamily: "monospace",
  whiteSpace: "nowrap",
  width: "35px",
  textAlign: "right",
  flexShrink: 0,
  fontSize: "0.75em",
}));

const HistoryEventDescription = styled(Box, {
  name: "HistoryEvent",
  slot: "Description",
  shouldForwardProp: (prop) => prop !== "eventCategory",
})<{ eventCategory?: string }>(({ theme, eventCategory }) => {
  const normalizedCategory = eventCategory?.replace(/\s+/g, "") ?? "";
  const color =
    (eventCategory && CATEGORY_COLORS[eventCategory]) ??
    CATEGORY_COLORS[normalizedCategory] ??
    null;

  return {
    flexGrow: 1,
    color: color ?? theme.palette.text.primary,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  };
});

const HistoryEventPrimaryDescription = styled(Typography, {
  name: "HistoryEvent",
  slot: "PrimaryDescription",
})(() => ({
  fontWeight: 500,
}));

const HistoryEventSecondaryList = styled("ul", {
  name: "HistoryEvent",
  slot: "SecondaryList",
})(({ theme }) => ({
  margin: 0,
  paddingLeft: 0,
  listStyleType: "none",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
}));

const HistoryEventSecondaryDescription = styled("li", {
  name: "HistoryEvent",
  slot: "SecondaryDescription",
})(({ theme }) => ({
  color: "inherit",
  opacity: 0.8,
  fontSize: "0.9em",
  lineHeight: 1.4,
}));

export const HistoryEvent: React.FC<Props> = ({ event }) => {
  const renderTimestamp = (date: Date) => {
    return (
      <Box display="flex" flexDirection="column">
        <HistoryEventTimestamp variant="body2">
          {`${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(
            2,
            "0",
          )}`}
        </HistoryEventTimestamp>
        <HistoryEventTimestamp variant="body2">
          {date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })}
        </HistoryEventTimestamp>
      </Box>
    );
  };

  const renderSecondaryDescriptionList = () => {
    if (event.secondaryDescriptions.length === 0) {
      return null;
    }

    return (
      <HistoryEventSecondaryList>
        {event.secondaryDescriptions.map((description, index) => (
          <HistoryEventSecondaryDescription key={`${description}-${index}`}>
            {description}
          </HistoryEventSecondaryDescription>
        ))}
      </HistoryEventSecondaryList>
    );
  };

  return (
    <HistoryEventRoot disablePadding>
      <Box display="flex" width="100%" alignItems="flex-start" gap={1}>
        {event.timestamp ? renderTimestamp(event.timestamp) : null}
        <HistoryEventDescription eventCategory={event.category}>
          <HistoryEventPrimaryDescription variant="body2">
            {event.primaryDescription}
          </HistoryEventPrimaryDescription>
          {renderSecondaryDescriptionList()}
        </HistoryEventDescription>
      </Box>
    </HistoryEventRoot>
  );
};
